import { ReactNode, useEffect, useRef, useState } from 'react';
import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';

import { useApiAuth } from '../../auth/ApiAuthProvider';
import { accountKey } from '../../auth/apiSession';

function recover(attempt: Promise<unknown>) {
  void attempt.catch(() => {
    /* The session retains a safe, visible error. */
  });
}

export default function UnAuthenticatedRedirect({
  children,
}: {
  children: ReactNode;
}) {
  const { instance, accounts, inProgress } = useMsal();
  const { session, accountId, token, error, busy } = useApiAuth();
  const account = instance.getActiveAccount() ?? accounts[0] ?? null;
  const currentAccountId = accountKey(account);
  const attemptedAccount = useRef<string>();
  const [unlockedAccount, setUnlockedAccount] = useState<string | null>(null);

  useEffect(() => {
    if (
      inProgress !== InteractionStatus.None ||
      currentAccountId !== accountId
    ) {
      return;
    }
    const key = currentAccountId ?? 'signed-out';
    if (attemptedAccount.current === key) {
      return;
    }
    attemptedAccount.current = key;
    const attempt = currentAccountId
      ? session.getToken()
      : session.signIn(true);
    void attempt.catch(() => {
      /* Recovery is displayed below. */
    });
  }, [session, accountId, currentAccountId, inProgress]);

  useEffect(() => {
    if (token && accountId) {
      setUnlockedAccount(accountId);
    }
  }, [token, accountId]);

  const disabled = busy || inProgress !== InteractionStatus.None;
  const showContent =
    currentAccountId &&
    currentAccountId === accountId &&
    (token || unlockedAccount === currentAccountId);

  return (
    <>
      {showContent ? (
        <div key={currentAccountId}>{children}</div>
      ) : (
        <Box component="main" sx={{ p: 4, textAlign: 'center' }}>
          <Typography role="status">
            {error
              ? 'Sign-in needs your attention.'
              : 'Restoring your session…'}
          </Typography>
        </Box>
      )}
      <Dialog open={Boolean(error)} aria-labelledby="session-recovery-title">
        <DialogTitle id="session-recovery-title">
          Restore your session
        </DialogTitle>
        <DialogContent>
          <DialogContentText role="alert">{error?.message}</DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            Signing in in this tab leaves the page. Unsaved changes may be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap' }}>
          <Button
            disabled={disabled}
            onClick={() => recover(session.signIn(true))}
          >
            Sign in in this tab
          </Button>
          {!error?.requiresInteraction && (
            <Button
              disabled={disabled}
              onClick={() => recover(session.getToken(true))}
            >
              Retry
            </Button>
          )}
          <Button
            variant="contained"
            disabled={disabled}
            onClick={() => recover(session.signIn())}
          >
            Sign in again
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
